import React from 'react';
import { FaUpload } from 'react-icons/fa';
import * as icons from "../../../../components/fileTypesIcons.js";
import folderSearch from '../../../../assets/icon/folder-search.svg';

/**
 * FileUploadUI Component
 *
 * Provides a drag-and-drop file upload interface for form questions.
 * Supports multiple file uploads and an optional maximum file count.
 * Displays all uploaded files with open/download/remove actions.
 *
 * @param {string}   questionId        - Unique identifier for the question
 * @param {object}   answerFiles       - Object containing uploaded files arrays by question ID
 * @param {function} setAnswerFiles    - Function to update the answerFiles state
 * @param {function} selectAnswerFile  - Function to open file selection modal
 * @param {function} handleFileOpen    - Function to open/view a file
 * @param {function} handleFileDownload- Function to download a file
 * @param {boolean}  isEmbedded        - Whether to display in compact mode
 * @param {boolean}  isSimulation      - Whether component is in simulation mode
 * @param {boolean}  readOnly          - Whether the component is in read-only mode
 * @param {object}   dragAnimationRef  - Reference for drag animation state
 * @param {function} _frDraggingEffect - Function to handle drag visual effects
 * @param {function} hideDragBubble    - Function to hide drag animation bubble
 * @param {object}   uploadManager     - Upload manager utility object
 * @param {string}   answersFolderId   - ID of the folder where answer files are stored
 * @param {function} currentUser       - Function to get current user information
 * @param {object}   MySwal            - SweetAlert2 instance for user notifications
 * @param {object}   colors            - Color scheme object for styling
 * @param {function} t                 - Translation function
 * @param {number|null} maxFiles       - Maximum number of files allowed (null = unlimited)
 */
const FileUploadUI = ({
  questionId,
  answerFiles,
  setAnswerFiles,
  handleInputChange,
  selectAnswerFile,
  handleFileOpen,
  handleFileDownload,
  isEmbedded = false,
  isSimulation = false,
  readOnly = false,
  dragAnimationRef,
  _frDraggingEffect,
  hideDragBubble,
  uploadManager,
  answersFolderId,
  currentUser,
  MySwal,
  colors,
  t,
  maxFiles = null
}) => {
  const files = answerFiles[questionId] || [];
  const canAddMore = maxFiles === null || files.length < maxFiles;
  const dragClassPrefix = isSimulation ? 'sim' : 'comp';

  const cleanupDragHighlight = (id) => {
    window.$(`#${dragClassPrefix}-drop-area-${id}`).removeClass(`${dragClassPrefix}-draggable-area`);
    hideDragBubble();
  };

  const appendFiles = (newFiles) => {
    setAnswerFiles(prev => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), ...newFiles]
    }));
  };

  const removeFile = (fileId) => {
    setAnswerFiles(prev => ({
      ...prev,
      [questionId]: (prev[questionId] || []).filter(f => f.Id !== fileId)
    }));
  };

  return (
    <div className={isEmbedded ? "mt-3 mb-0" : "mt-3 mb-0"}>

      {/* List of already uploaded files */}
      {files.length > 0 && (
        <div className="mb-2">
          {files.map((file) => {
            const fileIcon = icons.iconMapping(file.MimeType);
            return (
              <div key={file.Id} className="card mb-2">
                <div className="card-body p-2">
                  <div className="d-flex align-items-center">
                    <img src={fileIcon} alt="File icon" className="me-2" style={{ width: '24px', height: '24px' }} />
                    <div className="flex-grow-1">
                      <h6 className="mb-0 small">{file.Name}</h6>
                      {file.Size ? (
                        <small className="text-muted">{Math.round(file.Size / 1024)} KB</small>
                      ) : null}
                    </div>
                    <div className="btn-group ms-2">
                      {handleFileOpen && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleFileOpen(file)}
                          title={t('openFile', { ns: 'labels', defaultValue: 'Open File' })}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                      )}
                      {handleFileDownload && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleFileDownload(file)}
                          title={t('downloadFile', { ns: 'labels', defaultValue: 'Download File' })}
                        >
                          <i className="fa-solid fa-download"></i>
                        </button>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeFile(file.Id)}
                          title={t('removeFile', { ns: 'labels', defaultValue: 'Remove File' })}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload zone – shown only when more files can be added */}
      {canAddMore && (
        <div style={{ width: '100%' }}>
          <div className="d-flex align-items-center mb-2">
            <small className="text-muted flex-grow-1">
              {t('dragFilesHere', { ns: 'labels', defaultValue: 'Drag files here' })}{' '}
              {t('orClickToSelect', { ns: 'labels' })}
              {maxFiles !== null && (
                <span className="ms-1 text-secondary">({files.length}/{maxFiles})</span>
              )}
            </small>
            {!readOnly && (
              <img
                src={folderSearch}
                className="ms-2"
                title={t('chooseFiles', { ns: 'labels' })}
                style={{ cursor: 'pointer', width: '22px', height: '22px' }}
                onClick={() => selectAnswerFile(questionId)}
              />
            )}
          </div>

          <div
            className={`${dragClassPrefix}-draggable-container`}
            id={`${dragClassPrefix}-drop-area-${questionId}`}
            style={{
              width: '100%',
              minHeight: isEmbedded ? '70px' : '90px',
              cursor: readOnly ? 'default' : 'pointer'
            }}
            onClick={() => !readOnly && selectAnswerFile(questionId)}
            onDragOver={(e) => {
              if (readOnly) return;
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseOut={() => {
              if (readOnly) return;
              window.$(`#${dragClassPrefix}-drop-area-${questionId}`).removeClass(`${dragClassPrefix}-draggable-area`);
            }}
            onDragEnter={(e) => {
              if (readOnly) return;
              e.preventDefault();
              e.stopPropagation();
              window.$(`#${dragClassPrefix}-drop-area-${questionId}`).addClass(`${dragClassPrefix}-draggable-area`);
              _frDraggingEffect(e, true, questionId);
            }}
            onDragEnd={(e) => {
              if (readOnly) return;
              e.preventDefault();
              e.stopPropagation();
              cleanupDragHighlight(questionId);
            }}
            onDragLeave={(e) => {
              if (readOnly) return;
              e.preventDefault();
              e.stopPropagation();
              if (!e.currentTarget.contains(e.relatedTarget)) {
                window.$(`#${dragClassPrefix}-drop-area-${questionId}`).removeClass(`${dragClassPrefix}-draggable-area`);
              }
            }}
            onDrop={async (e) => {
              if (readOnly) return;
              cleanupDragHighlight(questionId);
              e.preventDefault();
              e.stopPropagation();

              try {
                const _files = await uploadManager.getAllFileEntries(e.dataTransfer.items);

                if (!_files.files || !_files.files.length) return;

                // Respect maxFiles limit
                const currentCount = (answerFiles[questionId] || []).length;
                const remainingSlots = maxFiles !== null ? maxFiles - currentCount : Infinity;

                if (remainingSlots <= 0) {
                  MySwal.fire({
                    title: t('warning', { ns: 'labels', defaultValue: 'Warning' }),
                    text: t('maxFilesReached', { ns: 'labels', defaultValue: 'Maximum number of files reached' }),
                    icon: 'warning'
                  });
                  return;
                }

                const filesToUpload = _files.files.slice(0, remainingSlots);

                if (!answersFolderId) {
                  MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('failedToRetrieveFiles', { ns: 'labels' }),
                    icon: 'error',
                    confirmButtonColor: colors?.danger || '#dc3545'
                  });
                  return;
                }

                MySwal.fire({
                  title: t('uploading', { ns: 'labels', defaultValue: 'Uploading...' }),
                  text: filesToUpload.map(f => f.name).join(', '),
                  allowOutsideClick: false,
                  showConfirmButton: false,
                  willOpen: () => { MySwal.showLoading(); }
                });

                uploadManager.fileUpload({
                  user: currentUser(),
                  parentId: answersFolderId,
                  files: filesToUpload,
                  onFileAdded: (uploaded) => {
                    if (Array.isArray(uploaded) && uploaded.length > 0) {
                      appendFiles(uploaded);
                      MySwal.fire({
                        title: t('fileUploadSuccessful', { ns: 'labels' }),
                        icon: 'success',
                        timer: 1500,
                        showConfirmButton: false
                      });
                    }
                  },
                  onError: (error) => {
                    console.error('Upload error:', error);
                    MySwal.fire({
                      title: t('error', { ns: 'labels' }),
                      text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
                      icon: 'error',
                      confirmButtonColor: colors?.danger || '#dc3545'
                    });
                  }
                });
              } catch (error) {
                console.error('Error processing dropped files:', error);
                MySwal.fire({
                  title: t('error', { ns: 'labels' }),
                  text: error.message || t('failedToRetrieveFiles', { ns: 'labels' }),
                  icon: 'error',
                  confirmButtonColor: colors?.danger || '#dc3545'
                });
              }
            }}
          >
            <div className="text-center">
              <FaUpload className="mb-2" size={isEmbedded ? 16 : 20} color="#6c757d" />
              <div className="small text-muted">
                {readOnly
                  ? t('readOnlyMode', { ns: 'labels', defaultValue: 'Read-only mode' })
                  : t('dropFileHere', { ns: 'labels' })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Max reached notice (non-readonly) */}
      {!canAddMore && !readOnly && (
        <small className="text-muted d-block mt-1">
          {t('maxFilesReached', { ns: 'labels', defaultValue: 'Maximum number of files reached' })} ({files.length}/{maxFiles})
        </small>
      )}
    </div>
  );
};

export default FileUploadUI;
